import React from "react";
import ChevronLeftRoundedIcon from "@mui/icons-material/ChevronLeftRounded";
import ChevronRightRoundedIcon from "@mui/icons-material/ChevronRightRounded";
import StarRoundedIcon from "@mui/icons-material/StarRounded";
import type SwiperInstance from "swiper";
import { Swiper, SwiperSlide } from "../Swiper";

import type { ContentCardData } from "../../@types/portalAluno";
import "swiper/swiper.min.css";

interface ContentDetailsCarouselProps {
  title: string;
  cards: ContentCardData[];
  activeContentId: number;
  onCardSelect: (card: ContentCardData) => void;
  onRequestClose: () => void;
}

const ContentDetailsCarousel = ({
  title,
  cards,
  activeContentId,
  onCardSelect,
  onRequestClose,
}: ContentDetailsCarouselProps) => {
  const swiperRef = React.useRef<SwiperInstance>();
  const focusFrameRef = React.useRef<number>();
  const initialIndex = Math.max(
    0,
    cards.findIndex((card) => card.id === activeContentId)
  );
  const [activeIndex, setActiveIndex] = React.useState(initialIndex);
  const [canGoBack, setCanGoBack] = React.useState(initialIndex > 0);
  const [canGoForward, setCanGoForward] = React.useState(
    initialIndex < cards.length - 1
  );

  const updateCarouselState = React.useCallback((swiper: SwiperInstance) => {
    setActiveIndex(swiper.activeIndex);
    setCanGoBack(!swiper.isBeginning);
    setCanGoForward(!swiper.isEnd);
  }, []);

  const slideToCard = React.useCallback(
    (index: number, speed = 300) => {
      const safeIndex = Math.max(0, Math.min(index, cards.length - 1));
      swiperRef.current?.slideTo(safeIndex, speed);
      setActiveIndex(safeIndex);
    },
    [cards.length]
  );

  React.useEffect(() => {
    slideToCard(initialIndex, 0);
  }, [initialIndex, slideToCard]);

  React.useEffect(
    () => () => {
      window.cancelAnimationFrame(focusFrameRef.current ?? 0);
    },
    []
  );

  if (cards.length === 0) {
    return (
      <div className="content-details-carousel content-details-carousel--empty">
        <p>Nenhum conteúdo disponível em {title}.</p>
      </div>
    );
  }

  return (
    <div
      className="content-details-carousel"
      role="region"
      aria-roledescription="carousel"
      aria-label={`Conteúdos de ${title}`}
    >
      <button
        type="button"
        className="content-details-carousel__arrow content-details-carousel__arrow--previous"
        onClick={() => swiperRef.current?.slidePrev()}
        disabled={!canGoBack}
        aria-label="Mostrar conteúdo anterior"
      >
        <ChevronLeftRoundedIcon aria-hidden="true" />
      </button>

      <Swiper
        className="content-details-carousel__viewport"
        slidesPerView="auto"
        spaceBetween={18}
        initialSlide={initialIndex}
        watchOverflow
        tabIndex={0}
        onSwiper={(swiper) => {
          swiperRef.current = swiper;
          updateCarouselState(swiper);
          focusFrameRef.current = window.requestAnimationFrame(() => {
            swiper.el.focus({ preventScroll: true });
          });
        }}
        onSlideChange={updateCarouselState}
        onResize={updateCarouselState}
        a11y={{
          containerMessage: `Conteúdos de ${title}`,
          slideLabelMessage: "Conteúdo {{index}} de {{slidesLength}}",
        }}
        onKeyDown={(event) => {
          if (event.key === "ArrowLeft") {
            event.preventDefault();
            swiperRef.current?.slidePrev();
          } else if (event.key === "ArrowRight") {
            event.preventDefault();
            swiperRef.current?.slideNext();
          } else if (event.key === "Home") {
            event.preventDefault();
            slideToCard(0);
          } else if (event.key === "End") {
            event.preventDefault();
            slideToCard(cards.length - 1);
          } else if (event.key === "Escape") {
            event.preventDefault();
            onRequestClose();
          }
        }}
      >
        {cards.map((card, index) => {
          const isCurrentContent = card.id === activeContentId;
          const metadata = [
            typeof card.modules === "number"
              ? `${card.modules} ${card.modules === 1 ? "módulo" : "módulos"}`
              : "",
            card.duration ?? "",
          ].filter(Boolean).join(" - ");
          const ratingLabel =
            typeof card.rating === "number" && card.rating > 0
              ? `, nota ${card.rating.toFixed(1)}`
              : "";

          return (
            <SwiperSlide
              key={card.id}
              style={
                {
                  "--carousel-card-index": index,
                } as React.CSSProperties
              }
            >
              <button
                type="button"
                className={`content-details-carousel__card${
                  isCurrentContent
                    ? " content-details-carousel__card--current"
                    : ""
                }`}
                data-carousel-card
                onClick={() => onCardSelect(card)}
                aria-label={`${card.title}${metadata ? `. ${metadata}` : ""}${ratingLabel}`}
                aria-current={isCurrentContent ? "page" : undefined}
              >
                <span
                  className="content-details-carousel__image"
                  role="img"
                  aria-label={card.imageAlt}
                  style={{
                    backgroundImage: card.imageUrl
                      ? `url(${card.imageUrl})`
                      : undefined,
                  }}
                />
                <strong>{card.title}</strong>
                {(metadata ||
                  (typeof card.rating === "number" && card.rating > 0)) && (
                  <span className="content-details-carousel__metadata">
                    {metadata && <span>{metadata}</span>}
                    {typeof card.rating === "number" && card.rating > 0 && (
                      <span aria-label={`Nota ${card.rating.toFixed(1)}`}>
                        <StarRoundedIcon aria-hidden="true" />
                        {card.rating.toFixed(1)}
                      </span>
                    )}
                  </span>
                )}
              </button>
            </SwiperSlide>
          );
        })}
      </Swiper>

      <button
        type="button"
        className="content-details-carousel__arrow content-details-carousel__arrow--next"
        onClick={() => swiperRef.current?.slideNext()}
        disabled={!canGoForward}
        aria-label="Mostrar próximo conteúdo"
      >
        <ChevronRightRoundedIcon aria-hidden="true" />
      </button>

      <span className="sr-only" role="status" aria-live="polite">
        Conteúdo {activeIndex + 1} de {cards.length}
      </span>
    </div>
  );
};

export default ContentDetailsCarousel;
